import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { ApiChallengeInline } from "griptonite-api-client";

import { useDeleteChallengeMutation, useChallengeEntries } from "@/hooks/challenges";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { useModal } from "./useModal";

export default function ConfirmDeleteChallenge({ challenge }: { challenge: ApiChallengeInline }) {
  const { close } = useModal();
  const venueUuid = useCurrentVenueUuid();
  const mutation = useDeleteChallengeMutation(venueUuid);
  const { data: entries } = useChallengeEntries(challenge.uuid);

  const onConfirm = () =>
    mutation.mutate(challenge.uuid, {
      onSuccess: () => {
        if (
          matchRoute({
            to: "/gym/$venueUuid/challenges/$challengeUuid",
            fuzzy: true,
          }) &&
          venueUuid
        ) {
          navigate({
            to: "/gym/$venueUuid/challenges",
            from: "/gym/$venueUuid/challenges/$challengeUuid",
            replace: true,
          });
        }
        close();
      },
      onError: (_err) => {
        alert("Failed to delete challenge. Please try again or contact support");
      },
    });

  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const hasEntries = entries && entries.length > 0;

  const canDelete = !hasEntries;

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>Are you sure?</DialogTitle>
      <DialogContent>
        <Typography sx={{ maxWidth: 350 }}>
          Deleting this challenge will remove all challenge data permanently. <br />
          {canDelete ? (
            <strong>(Once a challenge has entries, it cannot be deleted using this tool)</strong>
          ) : (
            <Alert color="danger" sx={{ marginTop: 2 }}>
              This challenge has entries and cannot be deleted using this tool. Please contact
              support if you need to remove it.
            </Alert>
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          color="danger"
          onClick={() => {
            onConfirm();
          }}
          sx={{ flex: 1 }}
          loading={mutation.isPending}
          disabled={!canDelete}
        >
          Continue
        </Button>
        <Button color="neutral" variant="soft" onClick={close} sx={{ flex: 1 }}>
          Cancel
        </Button>
      </DialogActions>
    </ModalDialog>
  );
}
